import { LitElement, css, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import resetStyle from '../../shared/components/html-content/reset.style';
import { ludsGradient } from '../../shared/styles/gradients';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import { ludsBaseFont, ludsButtonFontStyling } from '../../shared/styles/typography';
import { ButtonEvents, ButtonSizes, ButtonVariants } from '../../shared/types/button.model';
import { IconSizes } from '../../shared/types/icons.model';
import { setForegroundColor } from '../../shared/util/set-foreground-color';
import '../icon/icon';

/**
 * Button component
 */
@customElement('luds-button')
export class LudsButton extends LitElement {
  /** Specifies the text to be displayed on the button. */
  @property({ type: String }) text: string;
  /** Specifies the button type. Valid values are 'primary', 'secondary', 'flat', 'ghost', 'ghost-inverse', 'secondary-inverse. */
  @property({ type: String }) variant: ButtonVariants = 'primary';
  /** Specifies the button size. Valid values are 'extra-small', 'small', 'large'. If no value is provided, the size will default to 'small'. */
  @property({ type: String }) size: ButtonSizes = 'small';
  /** Specifies the icon to be displayed inside the button. */
  @property({ type: String }) icon?: string;
  /** If true, the button will be rendered in a disabled state and will not be clickable. */
  @property({ type: Boolean, reflect: true }) disabled = false;
  /** The qaId is used for testing purposes. */
  @property({ type: String, reflect: true }) 'data-qa-id': string;

  /** Specify the aria-label attribute for accessibility. */
  @property({ type: String }) a11yAriaLabel: string;

  /** Indicate whether the element has a popup menu. */
  @property({ type: Boolean }) a11yAriaHasPopup: boolean;

  /** Indicate whether the expandable element is currently expanded. */
  @property({ type: Boolean }) a11yAriaExpanded: boolean;

  /** Specify the ID of the element controlled by this component. */
  @property({ type: String }) a11yAriaControls: string;

  /** Specify the aria-pressed attribute for accessibility. */
  @property({ type: Boolean }) selected: boolean | undefined = undefined;

  static override styles = [
    overrideDesignTokens,
    resetStyle,
    ludsButtonFontStyling,
    ludsBaseFont,
    ludsGradient,
    css`
      :host {
        display: inline-block;
        --luds-btn-primary-background-colour: var(--theme-link-color, --colours-brand-primary);
        --luds-btn-primary-colour: var(--surfaces-surface-light);
        --luds-button-custom-padding-5: calc(var(--spacing-spacing-08) - 3px);
        --luds-button-custom-padding-7: calc(var(--spacing-spacing-08) - 1px);
        --luds-btn-box-shadow: 0px 0px 4px -1px var(--semantic-colour-tokens-stroke-stroke-brand);
        --luds-btn-focus-outline-color: var(--theme-link-color, --colours-brand-primary);
      }

      .btn {
        font-size: var(--luds-font-size-14);
        font-weight: 500;
        text-align: center;
        white-space: nowrap;
        cursor: pointer;
        border: 1px solid transparent;
        border-radius: var(--corners-s);
        transition-delay: 0s;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        transition-duration: 0.28s;
        transition-property: background-color, border-color, filter, color, fill;
      }

      .btn[disabled],
      .btn.disabled {
        cursor: not-allowed;
        opacity: 0.42;
      }

      /* Primary btn */
      .primary {
        background: var(--luds-btn-primary-background-colour);
        color: var(--foreground-color, var(--semantic-colour-tokens-text-text-on-dark-surface));
        fill: var(--foreground-color, var(--semantic-colour-tokens-text-text-on-dark-surface));

        &:hover {
          background-color: color-mix(in srgb, var(--luds-btn-primary-background-colour) 90%, black);
        }

        &:focus-visible {
          outline: 2px solid var(--luds-btn-focus-outline-color);
          outline-offset: 2px;
          background-color: color-mix(in srgb, var(--luds-btn-primary-background-colour) 90%, black);
        }

        &:active {
          background-color: color-mix(in srgb, var(--luds-btn-primary-background-colour) 80%, black);
        }

        &[disabled],
        &.disabled {
          background-color: var(--luds-btn-primary-background-colour);
          color: var(--luds-btn-primary-colour);
          fill: var(--luds-btn-primary-colour);
          box-shadow: none;
        }
      }

      /* Secondary btn */
      .secondary {
        color: var(--object-object-secondary);
        fill: var(--object-object-secondary);
        border: 1px solid var(--semantic-colour-tokens-stroke-stroke-primary);
        background: var(--semantic-colour-tokens-surface-surface-primary);

        &:hover {
          background: var(--semantic-colour-tokens-buttons-button-secondary-hover);
        }

        &:focus-visible {
          outline: 2px solid var(--luds-btn-focus-outline-color);
          outline-offset: 2px;
          background: var(--semantic-colour-tokens-buttons-button-secondary-hover);
        }

        &:active {
          border: 1px solid var(--semantic-colour-tokens-stroke-stroke-dark);
          background: var(--semantic-colour-tokens-buttons-button-secondary-active);
        }

        &[disabled],
        &.disabled {
          color: var(--object-object-secondary);
          fill: var(--object-object-secondary);
          background: var(--surfaces-surface-light);
          border: 1px solid var(--strokes-stroke-primary);
          box-shadow: none;
        }
      }

      .secondary-inverse {
        color: var(--semantic-colour-tokens-text-text-on-dark-surface);
        fill: var(--semantic-colour-tokens-icons-icon-on-dark-surface);
        border: 1px solid var(--semantic-colour-tokens-stroke-stroke-on-dark-surface);
        background: transparent;

        &:hover {
          background: color-mix(in srgb, var(--semantic-colour-tokens-buttons-button-inverse) 15%, transparent);
        }

        &:focus-visible {
          outline: 2px solid var(--luds-btn-focus-outline-color);
          outline-offset: 2px;
          background: color-mix(in srgb, var(--semantic-colour-tokens-buttons-button-inverse) 15%, transparent);
        }

        &:active {
          box-shadow: none;
          outline: none;
          background: color-mix(in srgb, var(--semantic-colour-tokens-buttons-button-inverse) 25%, transparent);
        }

        &[disabled],
        &.disabled {
          color: color-mix(in srgb, var(--semantic-colour-tokens-text-text-on-dark-surface) 90%, black);
          fill: color-mix(in srgb, var(--semantic-colour-tokens-icons-icon-on-dark-surface) 90%, black);
          background: transparent;
          box-shadow: none;
        }
      }

      /* Flat btn */
      .flat {
        color: var(--semantic-colour-tokens-text-text-brand);
        fill: var(--semantic-progress);
        background: var(--surfaces-surface-light);

        &:hover {
          color: color-mix(in srgb, var(--semantic-progress) 90%, black);
          fill: color-mix(in srgb, var(--semantic-progress) 90%, black);
          background: var(--surfaces-surface-light);
        }

        &:focus-visible {
          outline: 2px solid var(--luds-btn-focus-outline-color);
          outline-offset: 2px;
        }

        &:active {
          color: var(--semantic-progress-dark);
          fill: var(--semantic-progress-dark);
        }

        &[disabled],
        &.disabled {
          color: var(--semantic-progress);
          fill: var(--semantic-progress);
          background: var(--surfaces-surface-light);
          box-shadow: none;
        }
      }

      /* Ghost btn */
      .ghost {
        color: var(--semantic-colour-tokens-text-text-secondary);
        fill: var(--semantic-colour-tokens-icons-icon-default);
        background-color: transparent;

        &:hover {
          background: var(--semantic-colour-tokens-buttons-button-ghost-hover);
        }

        &:focus-visible {
          outline: 2px solid var(--luds-btn-focus-outline-color);
          outline-offset: 2px;
          background: var(--semantic-colour-tokens-buttons-button-ghost-hover);
        }

        &:active {
          color: var(--semantic-colour-tokens-text-text-primary);
          fill: var(--semantic-colour-tokens-icons-icon-default);
          background: var(--semantic-colour-tokens-buttons-button-ghost-active);
        }

        &[disabled],
        &.disabled {
          color: var(--object-object-secondary);
          fill: var(--object-object-secondary);
          background-color: transparent;
          box-shadow: none;
        }

        &[aria-pressed='true'] {
          background: var(--semantic-colour-tokens-buttons-button-ghost-active, #c7ccd1);
        }
      }

      /* Ghost Inverse btn */
      .ghost-inverse {
        color: var(--semantic-colour-tokens-text-text-on-dark-surface);
        fill: var(--semantic-colour-tokens-icons-icon-on-dark-surface);
        background: transparent;

        &:hover {
          background: color-mix(in srgb, var(--semantic-colour-tokens-buttons-button-inverse) 15%, transparent);
        }

        &:focus-visible {
          outline: 2px solid var(--luds-btn-focus-outline-color);
          outline-offset: 2px;
          background: color-mix(in srgb, var(--semantic-colour-tokens-buttons-button-inverse) 15%, transparent);
        }

        &:active {
          box-shadow: none;
          outline: none;
          background: color-mix(in srgb, var(--semantic-colour-tokens-buttons-button-inverse) 25%, transparent);
        }

        &[disabled],
        &.disabled {
          color: color-mix(in srgb, var(--colours-neutrals-white-negative) 90%, black);
          fill: color-mix(in srgb, var(--colours-neutrals-white-negative) 90%, black);
          background: transparent;
          box-shadow: none;
        }
      }

      /* Gradient btn */
      .gradient {
        background: var(--gradient-colors-45deg);
        color: var(--semantic-colour-tokens-text-text-on-dark-surface);
        fill: var(--semantic-colour-tokens-icons-icon-on-dark-surface);
        position: relative;
        transform-style: preserve-3d;
        /* Use pseudo-elements for the gradient effect */
        &::before {
          content: '';
          position: absolute;
          inset: -1px; /* Extends beyond the button's boundaries */
          background: var(--gradient-colors-45deg);
          border-radius: inherit; /* Match button's border radius */
          z-index: -1;
          transition: filter 0.28s ease-in-out;
        }

        &:hover {
          &::before {
            filter: brightness(0.9);
          }
        }

        &:focus-visible {
          outline: 2px solid var(--luds-btn-focus-outline-color);
          outline-offset: 2px;

          &::before {
            filter: brightness(0.9);
          }
        }

        &:active {
          &::before {
            filter: brightness(0.8);
          }
        }

        &[disabled],
        &.disabled {
          color: var(--semantic-colour-tokens-text-text-on-dark-surface);
          fill: var(--semantic-colour-tokens-icons-icon-on-dark-surface);
          box-shadow: none;
          opacity: 0.42;
        }
      }

      /* Different sizes of buttons */
      .extra-small {
        padding: var(--luds-button-custom-padding-5) var(--spacing-spacing-12);
        line-height: var(--luds-line-height-16);

        & luds-icon {
          margin-right: var(--spacing-spacing-08);
          margin-left: calc(-1 * var(--spacing-spacing-04));
        }

        &.btn-icon-only {
          padding: var(--luds-button-custom-padding-5);
        }
      }

      .small {
        padding: var(--luds-button-custom-padding-7) var(--spacing-spacing-16);
        line-height: var(--luds-line-height-20);

        & luds-icon {
          margin-right: var(--spacing-spacing-08);
          margin-left: calc(-1 * var(--spacing-spacing-04));
        }

        &.btn-icon-only {
          padding: var(--luds-button-custom-padding-7);
        }
      }

      .large {
        padding: var(--luds-button-custom-padding-7) var(--spacing-spacing-16); /* 8px (padding 7px + 1px border) */
        font-size: var(--luds-font-size-16);
        line-height: var(--luds-line-height-24);

        & luds-icon {
          margin-right: var(--spacing-spacing-08);
          margin-left: calc(-1 * var(--spacing-spacing-08));
        }

        &.btn-icon-only {
          padding: var(--luds-button-custom-padding-7);
        }
      }

      /* Button with icon + text */
      .btn-icon {
        display: flex;
        align-items: center;
      }

      /* Button with only icon */
      .btn-icon-only {
        display: flex;
        align-items: center;

        & luds-icon {
          margin: unset;
        }
      }
    `,
  ];

  @query('.primary')
  private buttonElement: HTMLButtonElement;

  @query('slot')
  private slotElement: HTMLSlotElement;

  private getSlotContent(): string {
    if (this.text) return this.text;

    if (!this.slotElement) return '';

    const nodes = this.slotElement.assignedNodes();
    return nodes
      .filter((node) => node.nodeType === Node.TEXT_NODE || node.nodeType === Node.ELEMENT_NODE)
      .map((node) => node.textContent?.trim())
      .join(' ')
      .trim();
  }

  override firstUpdated() {
    if (this.buttonElement) {
      setForegroundColor({
        element: this.buttonElement,
        lightColor: 'var(--semantic-colour-tokens-text-text-on-dark-surface)',
        darkColor: 'var(--semantic-colour-tokens-text-text-primary)',
      });
    }
  }

  handleClick() {
    if (this.disabled) return;
    const event = new Event(ButtonEvents.CLICK);
    this.dispatchEvent(event);
  }

  override render() {
    const textContent = this.getSlotContent();
    const { a11yAriaControls, a11yAriaExpanded, a11yAriaHasPopup, a11yAriaLabel } = this;
    const buttonClasses = {
      btn: true,
      [this.variant]: !!this.variant,
      [this.size]: !!this.size,
      'btn-icon': !!this.icon && !!textContent,
      'btn-icon-only': !!this.icon && !textContent,
    };
    let iconSize: IconSizes;

    switch (this.size) {
      case 'large':
        iconSize = 'medium';
        break;
      case 'small':
        iconSize = 'small';
        break;
      case 'extra-small':
        iconSize = 'extra-extra-small';
        break;
      default:
        iconSize = 'medium';
    }

    return html`
      <button
        class="${classMap(buttonClasses)}"
        .disabled="${this.disabled}"
        @click="${this.handleClick}"
        aria-label="${a11yAriaLabel}"
        aria-haspopup="${a11yAriaHasPopup}"
        aria-expanded="${a11yAriaExpanded}"
        aria-controls="${a11yAriaControls}"
        aria-pressed=${ifDefined(this.selected)}>
        ${this.icon ? html`<luds-icon icon="${this.icon}" size="${iconSize}" aria-hidden="true"></luds-icon>` : null}
        ${this.text ?? html`<slot></slot>`}
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-button': LudsButton;
  }
}
